import { FC, useEffect, useState } from 'react';
import { Box, Button, Grid, TextField, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { DatePicker } from '@material-ui/pickers';
import DateRangeIcon from '@material-ui/icons/DateRange';
import dayjs from 'dayjs';
import CheckIcon from '@material-ui/icons/Check';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Row } from '@tanstack/react-table';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import CustomSelect from '../../../components/CustomSelect';
import { DATE_FORMAT, SUBMITTING_DATE_FORMAT } from '../../../config/constants';
import { ConformityDto, ConformityRowDto } from '../../../api/data-contracts';
import { useStyles } from '../styles';
import { updateConformity } from '../../../services/TasksService';
import { queryKeys } from '../../../react-query/constants';
import { getBackendErrorMessage } from '../../../config/utils';
import { RootReducer, TasksReducer } from '../../../models/Redux';

type ConformityDetailPageProps = {
    row: Row<ConformityRowDto>;
};

const ConformityDetailPage: FC<ConformityDetailPageProps> = ({ row }) => {
    const classes = useStyles();
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { id } = useParams<{ id?: string }>();
    const { conformityStatuses } = useSelector<RootReducer, TasksReducer>(state => state.tasks);
    const [startDate, setStartDate] = useState<string>();
    const [newStatus, setNewStatus] = useState<number>();
    const [comment, setComment] = useState<string>();
    const [description, setDescription] = useState<string>();

    useEffect(() => {
        setStartDate(row.original.startDate);
        setNewStatus(row.original.status);
        setComment(row.original.currentComment);
        setDescription(row.original.description);
    }, [row]);

    const statusChangeMutation = useMutation(
        ({
            confId,
            status,
            comment,
            startDate,
            description,
        }: {
            confId: number;
            status: number;
            comment?: string;
            startDate?: string;
            description?: string;
        }) =>
            updateConformity(confId, parseInt((id as unknown) as string, 10) || 0, {
                comment,
                status,
                description,
                date: startDate ? dayjs(startDate).format(SUBMITTING_DATE_FORMAT) : undefined,
            }),
        {
            onSuccess: (data: ConformityDto) => {
                queryClient.invalidateQueries([queryKeys.conformities]);
                enqueueSnackbar('Conformity updated successfully', { variant: 'success' });

                setNewStatus(data.status);
                setStartDate(data.startDate);
                setComment(data.comment);
                setDescription(data.description);
            },
            onError: (error: unknown) => {
                const errorMessage =
                    error instanceof Error
                        ? getBackendErrorMessage(error as AxiosError)
                        : 'Failed to update status';
                enqueueSnackbar(t(`errorsEn:${errorMessage}`), { variant: 'error' });
            },
        },
    );

    return (
        <Box className={classes.expandedRow}>
            <Grid container spacing={4} justify="flex-end">
                <Grid item xs={12} md={6}>
                    <Box className={classes.subComponentBox}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={4}>
                                <Typography className={classes.rowLabel}>project</Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Typography className={classes.rowValue}>
                                    {row.original.projectName}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography className={classes.rowLabel}>contract type</Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Typography className={classes.rowValue}>
                                    {row.original.contractType}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography className={classes.rowLabel}>article</Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Typography className={classes.rowValue}>{row.original.article1}</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography className={classes.rowLabel}>previous status</Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Typography className={classes.rowValue}>
                                    <Box
                                        className={clsx(classes.statusIndicator, {
                                            [classes.openStatusIndicator]:
                                                row.original.previousStatus &&
                                                row.original.previousStatus?.toLowerCase() !== 'conform',
                                            [classes.closedStatusIndicator]:
                                                row.original.previousStatus?.toLowerCase() === 'conform',
                                        })}
                                    />{' '}
                                    {row.original.previousStatus}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box display="flex" flexDirection="row" alignItems="center" height="100%">
                                    <Typography className={classes.rowLabel}>start date</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <DatePicker
                                    allowKeyboardControl={false}
                                    inputVariant="filled"
                                    format={DATE_FORMAT}
                                    InputProps={{ endAdornment: <DateRangeIcon /> }}
                                    value={startDate}
                                    onChange={date => {
                                        if (date) setStartDate(dayjs(date).format(SUBMITTING_DATE_FORMAT));
                                    }}
                                    className={classes.datepicker}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box display="flex" flexDirection="row" alignItems="center" height="100%">
                                    <Typography className={classes.rowLabel}>conform</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <CustomSelect
                                    id="new-status"
                                    inputName="new-status"
                                    availableData={conformityStatuses.map(status => ({
                                        ...status,
                                        name: (
                                            <Typography className={classes.statusSelectText}>
                                                <Box
                                                    className={clsx(classes.statusIndicator, {
                                                        [classes.openStatusIndicator]:
                                                            status.name?.toLowerCase() !== 'conform',
                                                        [classes.closedStatusIndicator]:
                                                            status.name?.toLowerCase() === 'conform',
                                                    })}
                                                />
                                                {status.name}
                                            </Typography>
                                        ),
                                    }))}
                                    value={newStatus}
                                    variant="filled"
                                    selectClass={clsx(classes.statusSelect, classes.selectInput)}
                                    iconClass={classes.statusSelectIcon}
                                    onChange={value => setNewStatus(value as number)}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box className={classes.subComponentBox}>
                        <Typography className={classes.subComponentBoxTitle}>description</Typography>
                        <TextField
                            name="description"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            variant="filled"
                            multiline
                            rows={8}
                            rowsMax={8}
                            classes={{ root: classes.textField }}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box className={classes.subComponentBox}>
                        <Typography className={classes.subComponentBoxTitle}>comment</Typography>
                        <TextField
                            name="comment"
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                            variant="filled"
                            multiline
                            rows={6}
                            rowsMax={6}
                            classes={{ root: classes.textField }}
                            style={{ marginBottom: '2rem' }}
                        />
                        <Typography className={classes.subComponentBoxTitle}>previous comment</Typography>
                        <Typography className={classes.subComponentBoxText}>
                            {row.original.previousComment}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>

            <Button
                variant="contained"
                color="primary"
                endIcon={<CheckIcon />}
                disabled={statusChangeMutation.isLoading}
                onClick={() => {
                    statusChangeMutation.mutate({
                        confId: row.original.id as number,
                        status: newStatus as number,
                        comment,
                        startDate,
                        description,
                    });
                }}
            >
                confirm
            </Button>
        </Box>
    );
};

export default ConformityDetailPage;
